import { Icon } from '@amelio/ui-library';

import type { ChoiceChipOption } from '@/components/ChoiceChips/ChoiceChips';
import type { EmployeeNoteCategory } from '@/types/employee-note';

import { ChoiceChips } from '@/components/ChoiceChips/ChoiceChips';
import { useAmelioTranslations } from '@/hooks';
import { EMPLOYEE_NOTE_CATEGORIES } from '@/types/employee-note';

/** The chip that shows every folder at once; `null` on the wire means "no category filter". */
export const ALL_FOLDERS = 'all';

export type NoteFolder = EmployeeNoteCategory | typeof ALL_FOLDERS;

interface NoteFolderTabsProps {
    selected: NoteFolder;
    onSelect: (folder: NoteFolder) => void;
    dataTestId?: string;
}

/**
 * Every chip wears the same folder mark, as in the prototype; it says nothing a reader needs.
 *
 * The colour is inherited, never set: `Icon` falls back to `Colors.BASE` when a caller says
 * nothing, and a chip is not a filled library button, so nothing hands it a lighter default. On
 * the picked chip -- navy ground, white text -- that fallback left the folder at 1.15:1 against
 * its background, below the 3:1 a graphical object owes. Inheriting `currentColor` puts it at
 * 12.96:1 when picked, and leaves the idle chips exactly as they were.
 */
const FolderMark = () => (
    <Icon
        name={'files-folder'}
        size={'sm'}
        color={'inherit'}
        ariaHidden
    />
);

/**
 * The folders of the "Notes" tab: "all", then one per category. One is always picked, so a
 * re-click keeps it rather than clearing it. The prototype's per-folder counts wait for the
 * server to serve them: `ChoiceChipOption` already carries a `count`, and the day a route
 * answers them the chips take it without changing shape.
 */
export const NoteFolderTabs = ({
    selected,
    onSelect,
    dataTestId = 'note-folders',
}: NoteFolderTabsProps) => {
    const { t } = useAmelioTranslations();

    const options: ChoiceChipOption<NoteFolder>[] = [
        {
            value: ALL_FOLDERS,
            label: t('performanceProfile.notesTab.folderAll'),
            icon: <FolderMark />,
        },
        ...EMPLOYEE_NOTE_CATEGORIES.map((category) => ({
            value: category,
            label: t(`performanceProfile.employeeNotes.categories.${category}`),
            icon: <FolderMark />,
        })),
    ];

    return (
        <ChoiceChips
            options={options}
            selected={[selected]}
            onToggle={(folder) => onSelect(folder)}
            ariaLabel={t('performanceProfile.notesTab.foldersAria')}
            dataTestId={dataTestId}
        />
    );
};
